/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { UserAuthManager, isMockAuthEnabled } from "./authentication";
import { globalErrorType } from "./ErrorContext";
import { getAuthToken } from "@services/auth/auth";
import { getRequiredEnvVar } from "@shared/utils/validate-env";
import { forgetClearance } from '@shared/utils/antibot-clearance-store';
import { clearAllDrafts } from '@shared/utils/form-draft-store';
import { getStoredSupportSession } from '@/features/support-access/support-session';

let _signoutInProgress = false;

/**
 * Returns whether a signout redirect is currently in flight.
 */
export function getIsSignoutInProgress(): boolean {
  return _signoutInProgress;
}

/**
 * Sets the module-level flag tracking whether a signout is in progress.
 *
 * @param value - `true` while the signout redirect is active, `false` otherwise.
 */
export function setSignoutInProgress(value: boolean): void {
  _signoutInProgress = value;
}

/**
 * Drops any pending post-login return path.
 *
 * Signing out is a deliberate exit, so the page the user happened to be on must
 * not be replayed at the next login — on a shared machine that would drop the
 * next person straight into the previous session's screen.
 *
 * @returns {void}
 */
function forgetReturnPath(): void {
  try {
    localStorage.removeItem("returnPath");
  } catch (error) {
    // Handle storage errors gracefully (e.g., when consent is rejected)
    console.warn('Failed to clear return path:', error);
  }
}

/**
 * Best-effort server-side logout.
 * Sends a POST to authien's /logout to invalidate the server session.
 * Silently swallows errors so it never blocks the signout flow.
 */
async function tryServerLogout(): Promise<void> {
  // A support session holds the customer's token, not a login of its own:
  // there is no Authien session behind it to end.
  if (getStoredSupportSession()) return;
  try {
    const token = getAuthToken();
    if (!token) return;
    const authority = getRequiredEnvVar('REACT_APP_AUTHORITY_URL');
    const redirectUri = window.location.origin;
    await fetch(`${authority}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ post_logout_redirect_uri: redirectUri }),
    });
  } catch {
    // Fire-and-forget: server-side session cleanup is best-effort
  }
}

/**
 * Executes the full client-side signout: marks signout in progress, drops the
 * stale return path, performs a best-effort server logout, removes the local
 * user, and lands the visitor on the public landing page.
 *
 * The landing page — not the identity provider's login screen — is the correct
 * destination: sending the user straight back to a login form after they asked
 * to leave reads as if the signout failed, and traps them in the auth flow.
 *
 * Throws when invoked in mock-auth mode.
 */
export async function performSignout(): Promise<void> {
  if (isMockAuthEnabled()) {
    throw new Error('Cannot perform signout in mock mode. Use the mock authentication provider instead.');
  }
  _signoutInProgress = true;
  forgetReturnPath();
  // The clearance is bound to the bearer being discarded here. Dropping it keeps
  // it from lingering for whoever signs in next on a shared machine.
  forgetClearance();
  // Local form drafts hold what the user typed (including third-party contacts):
  // they must not outlive the session on a shared machine.
  clearAllDrafts();
  await tryServerLogout();
  await UserAuthManager.removeUser();
  // Full document navigation (not a router push) so every in-memory auth store,
  // cached query and OIDC timer is torn down with the page. The language is not
  // carried in the URL: i18next reads it back from localStorage on boot.
  window.location.replace('/');
}

/**
 * Handles signout flow with error handling.
 *
 * @param {Function} addLoading - Loading add function
 * @param {Function} removeLoading - Loading remove function
 * @param {Function} setGlobalError - Error setter
 * @returns {Promise<void>}
 */
export async function handleSignoutFlow(
  addLoading: () => void,
  removeLoading: () => void,
  setGlobalError: (error: globalErrorType) => void
): Promise<void> {
  try {
    addLoading();
    await performSignout();
  } catch (error) {
    setGlobalError(error as globalErrorType);
  } finally {
    removeLoading();
  }
}
